/**
 * Price History Tab - Audit trail of all price changes
 *
 * Features:
 * - Filter by vendor, user, date range
 * - View detailed breakdown of price change batches
 * - Rollback capability (with confirmation)
 * - Export history to CSV (TODO)
 */

import React, { useState } from 'react'
import {
  Space,
  Card,
  Select,
  DatePicker,
  Button,
  Table,
  Tag,
  Typography,
  Modal,
  Descriptions,
  Drawer,
} from 'antd'
import { DownloadOutlined, UndoOutlined, HistoryOutlined } from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import { usePriceHistory, useRollbackPriceChange } from '../hooks/usePriceData'
import type { PriceHistoryEntry, HistoryFilters } from '../types'

const { Text } = Typography
const { RangePicker } = DatePicker

export const PriceHistoryTab: React.FC = () => {
  const [filters, setFilters] = useState<HistoryFilters>({})
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<PriceHistoryEntry | null>(null)

  const { data: history, isLoading } = usePriceHistory(filters)
  const rollbackMutation = useRollbackPriceChange()

  const handleExportHistory = () => {
    // TODO: Implement CSV export
    console.log('Export history clicked')
  }

  const handleRollback = (entry: PriceHistoryEntry) => {
    Modal.confirm({
      title: 'Rollback Price Changes',
      content: (
        <Space direction="vertical">
          <Text>
            This will rollback <Text strong>{entry.skusAffected} price changes</Text> from{' '}
            {new Date(entry.timestamp).toLocaleString()}.
          </Text>
          <Text type="warning">This action cannot be undone.</Text>
        </Space>
      ),
      okText: 'Rollback',
      okType: 'danger',
      onOk: async () => {
        await rollbackMutation.mutateAsync(entry.uid)
      },
    })
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'bulk_approval':
        return '✓'
      case 'manual_edit':
        return '✎'
      case 'rule_change':
        return '⚙'
      case 'cost_update':
        return '$'
      case 'rollback':
        return '↶'
      default:
        return '•'
    }
  }

  const getActionColor = (action: string): string => {
    switch (action) {
      case 'bulk_approval':
        return 'green'
      case 'manual_edit':
        return 'blue'
      case 'rule_change':
        return 'purple'
      case 'cost_update':
        return 'orange'
      case 'rollback':
        return 'red'
      default:
        return 'default'
    }
  }

  const columns: TableColumnsType<PriceHistoryEntry> = [
    {
      title: 'Date/Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date: string) => (
        <Space direction="vertical" size={0}>
          <Text>{new Date(date).toLocaleDateString()}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {new Date(date).toLocaleTimeString()}
          </Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (action: string) => (
        <Tag color={getActionColor(action)} icon={<span>{getActionIcon(action)}</span>}>
          {action.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Bulk Approval', value: 'bulk_approval' },
        { text: 'Manual Edit', value: 'manual_edit' },
        { text: 'Rule Change', value: 'rule_change' },
        { text: 'Cost Update', value: 'cost_update' },
        { text: 'Rollback', value: 'rollback' },
      ],
      onFilter: (value, record) => record.action === value,
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 150,
    },
    {
      title: 'SKUs Affected',
      dataIndex: 'skusAffected',
      key: 'skusAffected',
      width: 120,
      render: (count: number) => <Text strong>{count}</Text>,
      sorter: (a, b) => a.skusAffected - b.skusAffected,
    },
    {
      title: 'Avg Change',
      dataIndex: 'avgPriceChange',
      key: 'avgPriceChange',
      width: 120,
      render: (change: number) => (
        <Tag color={change > 0 ? 'green' : change < 0 ? 'red' : 'default'}>
          {change > 0 ? '+' : ''}
          {change.toFixed(1)}%
        </Tag>
      ),
      sorter: (a, b) => a.avgPriceChange - b.avgPriceChange,
    },
    {
      title: 'Impact',
      dataIndex: 'totalImpact',
      key: 'totalImpact',
      width: 120,
      render: (impact: number) => (
        <Text style={{ color: impact > 0 ? '#52c41a' : impact < 0 ? '#ff4d4f' : undefined }}>
          {impact > 0 ? '+' : ''}${impact.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.totalImpact - b.totalImpact,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag
          color={
            status === 'live' ? 'green' : status === 'rolled_back' ? 'red' : 'blue'
          }
        >
          {status.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              setSelectedEntry(record)
              setDetailDrawerOpen(true)
            }}
          >
            Details
          </Button>
          {record.status === 'live' && (
            <Button
              size="small"
              danger
              icon={<UndoOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleRollback(record)
              }}
            >
              Rollback
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Filters */}
      <Card size="small">
        <Space wrap>
          <Select
            placeholder="All Vendors"
            value={filters.vendorId}
            onChange={(v) => setFilters({ ...filters, vendorId: v })}
            style={{ width: 200 }}
            allowClear
          >
            {/* TODO: Load from vendors API */}
            <Select.Option value="vendor-1">ABC Corp</Select.Option>
            <Select.Option value="vendor-2">XYZ Inc</Select.Option>
          </Select>
          <Select
            placeholder="All Users"
            value={filters.userId}
            onChange={(v) => setFilters({ ...filters, userId: v })}
            style={{ width: 200 }}
            allowClear
          >
            {/* TODO: Load from users API */}
            <Select.Option value="user-123">Doug Mitchell</Select.Option>
            <Select.Option value="system">System</Select.Option>
          </Select>
          <RangePicker
            value={filters.dateRange as any}
            onChange={(dates) =>
              setFilters({
                ...filters,
                dateRange: dates ? [dates[0]!.toISOString(), dates[1]!.toISOString()] : null,
              })
            }
          />
          <Button icon={<DownloadOutlined />} onClick={handleExportHistory}>
            Export CSV
          </Button>
        </Space>
      </Card>

      {/* History Table */}
      <Table
        columns={columns}
        dataSource={history?.map((h) => ({ ...h, key: h.uid })) || []}
        loading={isLoading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `${total} entries`,
        }}
        scroll={{ x: 'max-content' }}
      />

      {/* History Detail Drawer */}
      <Drawer
        title="Price Change Details"
        placement="right"
        width={500}
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
      >
        {selectedEntry && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small" title="Change Summary">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Action">
                  <Tag color={getActionColor(selectedEntry.action)}>
                    {selectedEntry.action.replace(/_/g, ' ').toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="User">{selectedEntry.user}</Descriptions.Item>
                <Descriptions.Item label="Timestamp">
                  {new Date(selectedEntry.timestamp).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      selectedEntry.status === 'live'
                        ? 'green'
                        : selectedEntry.status === 'rolled_back'
                        ? 'red'
                        : 'blue'
                    }
                  >
                    {selectedEntry.status.replace(/_/g, ' ').toUpperCase()}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Impact">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="SKUs Affected">
                  <Text strong>{selectedEntry.skusAffected}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Avg Price Change">
                  <Tag color={selectedEntry.avgPriceChange > 0 ? 'green' : 'red'}>
                    {selectedEntry.avgPriceChange > 0 ? '+' : ''}
                    {selectedEntry.avgPriceChange.toFixed(1)}%
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Margin Impact">
                  <Text
                    style={{
                      color:
                        selectedEntry.totalImpact > 0
                          ? '#52c41a'
                          : selectedEntry.totalImpact < 0
                          ? '#ff4d4f'
                          : undefined,
                    }}
                  >
                    {selectedEntry.totalImpact > 0 ? '+' : ''}$
                    {selectedEntry.totalImpact.toFixed(2)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {selectedEntry.metadata && Object.keys(selectedEntry.metadata).length > 0 && (
              <Card size="small" title="Additional Details">
                <Descriptions column={1} size="small">
                  {selectedEntry.metadata.vendorId && (
                    <Descriptions.Item label="Vendor">
                      {selectedEntry.metadata.vendorId}
                    </Descriptions.Item>
                  )}
                  {selectedEntry.metadata.ruleId && (
                    <Descriptions.Item label="Rule">
                      {selectedEntry.metadata.ruleId}
                    </Descriptions.Item>
                  )}
                  {selectedEntry.metadata.sourceFile && (
                    <Descriptions.Item label="Source File">
                      {selectedEntry.metadata.sourceFile}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {selectedEntry.status === 'live' && (
              <Card size="small">
                <Button
                  danger
                  icon={<UndoOutlined />}
                  block
                  onClick={() => {
                    setDetailDrawerOpen(false)
                    handleRollback(selectedEntry)
                  }}
                >
                  Rollback This Change
                </Button>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </Space>
  )
}
